import { useState } from 'react';
import { Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../api.ts';
import { registerSchema } from '../../../shared/schemas.ts';
import type { z } from 'zod';
import { applyServerErrors, ErrorBox, Field, Help, Spinner, useDocumentTitle } from '../../components/ui.tsx';

type Values = z.input<typeof registerSchema>;

export function RegisterPage() {
  useDocumentTitle('Rekisteröidy');
  const [done, setDone] = useState<string | null>(null);
  const { register, handleSubmit, formState, setError } = useForm<Values, unknown, z.output<typeof registerSchema>>({
    resolver: zodResolver(registerSchema),
    defaultValues: { genre: 'OM', userClass: 'Yleisluokka', address2: '' },
  });
  const e = formState.errors;
  const submit = useMutation({
    mutationFn: (d: z.output<typeof registerSchema>) => api.post<{ id: string }>('/auth/register', d),
    onSuccess: (r) => setDone(r.id),
    onError: (err) => applyServerErrors(err, setError),
  });

  if (done) {
    return (
      <div className="auth-wrap">
        <div className="auth-card card">
          <h1>Kiitos rekisteröitymisestä!</h1>
          <p>
            Tunnuksesi <span className="call">{done}</span> on aktivoitu. Voit nyt kirjautua palveluun.
          </p>
          <Link className="btn btn-primary" to="/login">
            Kirjaudu sisään
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card wide card">
        <div className="row" style={{ marginBottom: 6 }}>
          <h1 style={{ margin: 0 }}>Rekisteröidy</h1>
          <span className="spacer" />
          <Help name="Rekisteröinti" />
        </div>
        <p className="muted">Kaikki tiedot lisenssin päivämäärää myöten tarvitaan, jotta voimme koota taulukot esim. tuoreille perusluokkalaisille.</p>
        <form className="form" onSubmit={handleSubmit((d) => submit.mutate(d))} noValidate>
          <div className="form-grid">
            <Field label="Kutsu (isoilla kirjaimilla)" error={e.id} htmlFor="id">
              <input id="id" className="input mono" autoCapitalize="characters" {...register('id')} />
            </Field>
            <Field label="Nimi" error={e.name} htmlFor="name">
              <input id="name" className="input" autoComplete="name" {...register('name')} />
            </Field>
            <Field label="Sähköposti" error={e.email} htmlFor="email">
              <input id="email" className="input" type="email" autoComplete="email" {...register('email')} />
            </Field>
            <Field label="Katuosoite" error={e.address1} htmlFor="address1">
              <input id="address1" className="input" autoComplete="street-address" {...register('address1')} />
            </Field>
            <Field label="Postinumero" error={e.postcode} htmlFor="postcode">
              <input id="postcode" className="input" autoComplete="postal-code" {...register('postcode')} />
            </Field>
            <Field label="Kaupunki" error={e.city} htmlFor="city">
              <input id="city" className="input" autoComplete="address-level2" {...register('city')} />
            </Field>
            <Field label="Lisenssi saatu" error={e.licenceDate} htmlFor="licenceDate">
              <input id="licenceDate" className="input" type="date" {...register('licenceDate')} />
            </Field>
          </div>
          <div className="grid grid-2">
            <fieldset>
              <legend>Luokka</legend>
              <div className="radio-group">
                <label className="check">
                  <input type="radio" value="Yleisluokka" {...register('userClass')} /> Yleisluokka
                </label>
                <label className="check">
                  <input type="radio" value="Perusluokka" {...register('userClass')} /> Perusluokka
                </label>
              </div>
            </fieldset>
            <fieldset>
              <legend>Sukupuoli</legend>
              <div className="radio-group">
                <label className="check">
                  <input type="radio" value="OM" {...register('genre')} /> OM
                </label>
                <label className="check">
                  <input type="radio" value="YL" {...register('genre')} /> YL
                </label>
              </div>
            </fieldset>
          </div>
          <div className="form-grid">
            <Field label="Salasana" error={e.password} htmlFor="password">
              <input id="password" className="input" type="password" autoComplete="new-password" {...register('password')} />
            </Field>
            <Field label="Salasana uudelleen" error={e.passwordConfirm} htmlFor="passwordConfirm">
              <input id="passwordConfirm" className="input" type="password" autoComplete="new-password" {...register('passwordConfirm')} />
            </Field>
          </div>
          <ErrorBox error={submit.error} />
          <div className="form-actions">
            <button className="btn btn-primary" type="submit" disabled={submit.isPending}>
              {submit.isPending ? <Spinner /> : 'Rekisteröidy'}
            </button>
            <Link to="/login" className="btn btn-ghost">
              Takaisin kirjautumiseen
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
